import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiChevronRight } from 'react-icons/fi';
import { Layout } from './Layout';
import { cn } from '../../lib/cn';

export interface Crumb {
  label: string;
  href?: string;
}

const LARGURAS = {
  estreita: 'max-w-3xl',
  media: 'max-w-4xl',
  larga: 'max-w-6xl',
} as const;

interface PortalPageProps {
  /** Título principal exibido no hero. */
  titulo: string;
  /** Linha de apoio abaixo do título. */
  subtitulo?: string;
  /** Ícone exibido na caixa à esquerda do título. */
  icone?: ReactNode;
  /** Trilha de navegação acima do título. */
  breadcrumbs?: Crumb[];
  /** Link de voltar (renderiza seta + "Voltar"). Use isto OU onVoltar. */
  voltarHref?: string;
  /** Handler de voltar (renderiza seta + "Voltar"). */
  onVoltar?: () => void;
  /** Rótulo do voltar (padrão "Voltar"). */
  voltarLabel?: string;
  /** Ações no canto direito do hero (botões claros). */
  acoes?: ReactNode;
  /** Conteúdo extra dentro do hero, abaixo do título (ex.: pílulas, métricas). */
  heroExtra?: ReactNode;
  /** Largura do container. Padrão "estreita" (formulários). */
  largura?: keyof typeof LARGURAS;
  /** Quando true, não envolve em <Layout> (header/footer). */
  semLayout?: boolean;
  children: ReactNode;
}

/**
 * Casca padrão das páginas do portal: header/footer (Layout) + hero institucional
 * com gradiente, ícone, título e trilha + container de conteúdo sobreposto.
 * Substitui os antigos wrappers `min-h-screen bg-slate-100` + `<h1>` avulso.
 */
export function PortalPage({
  titulo, subtitulo, icone, breadcrumbs, voltarHref, onVoltar, voltarLabel = 'Voltar',
  acoes, heroExtra, largura = 'estreita', semLayout, children,
}: PortalPageProps) {
  const larguraCls = LARGURAS[largura];
  const mostrarVoltar = Boolean(voltarHref || onVoltar);

  const conteudo = (
    <>
      <div className="bg-gradient-to-br from-institutional via-institutional-dark to-slate-900 text-white">
        <div className={cn(larguraCls, 'mx-auto px-4 pt-7 pb-12')}>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-blue-100/80">
              {breadcrumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1">
                  {c.href ? (
                    <Link to={c.href} className="transition hover:text-white">{c.label}</Link>
                  ) : (
                    <span className="text-white">{c.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 && <FiChevronRight size={11} className="opacity-60" />}
                </span>
              ))}
            </nav>
          )}

          {mostrarVoltar && (
            voltarHref ? (
              <Link
                to={voltarHref}
                className="mb-3 inline-flex items-center gap-1.5 text-sm text-blue-100/90 transition hover:text-white"
              >
                <FiArrowLeft size={15} /> {voltarLabel}
              </Link>
            ) : (
              <button
                type="button"
                onClick={onVoltar}
                className="mb-3 inline-flex items-center gap-1.5 text-sm text-blue-100/90 transition hover:text-white"
              >
                <FiArrowLeft size={15} /> {voltarLabel}
              </button>
            )
          )}

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
              {icone !== undefined && (
                <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white shadow-sm ring-1 ring-white/20 backdrop-blur sm:flex">
                  {icone}
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{titulo}</h1>
                {subtitulo && (
                  <p className="mt-1.5 max-w-2xl text-sm text-blue-100/90 sm:text-[15px]">{subtitulo}</p>
                )}
              </div>
            </div>
            {acoes && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{acoes}</div>}
          </div>

          {heroExtra && <div className="mt-5">{heroExtra}</div>}
        </div>
      </div>

      <div className={cn(larguraCls, 'mx-auto -mt-6 px-4 pb-14')}>{children}</div>
    </>
  );

  if (semLayout) {
    return <div className="min-h-screen bg-slate-50">{conteudo}</div>;
  }
  return <Layout>{conteudo}</Layout>;
}
